<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

defineProps<{
  open: boolean;
  report: any;
}>();

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (event: "confirm"): void;
}>();

function close() {
  emit("update:open", false);
}

function confirm() {
  emit("confirm");
}
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="max-w-md rounded-3xl p-6 bg-white shadow-2xl border border-slate-100 text-left">
      <DialogHeader class="space-y-3">
        <div class="h-12 w-12 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center">
          <DMSuiteIcon :icon="DASHBOARD_ICONS.eventReport" :size="24" custom-class="text-indigo-600" />
        </div>
        <div class="space-y-1">
          <DialogTitle class="text-lg font-black text-slate-800">
            Download Monthly Report
          </DialogTitle>
          <DialogDescription class="text-xs text-slate-400 font-semibold leading-relaxed">
            Prepare and download the comprehensive marketing performance summary for {{ report?.report_period }}.
          </DialogDescription>
        </div>
      </DialogHeader>

      <div class="py-4 space-y-3">
        <p class="text-sm font-extrabold text-slate-700">
          This report includes:
        </p>
        <ul class="space-y-2">
          <li
            v-for="item in [
              'Service progress summary',
              'Service health diagnostics',
              'Completed tasks breakdown',
              'Pending client action status',
              'Package performance indicators',
              'Latest update trail and logs'
            ]"
            :key="item"
            class="flex items-center gap-2 text-xs text-slate-500 font-semibold"
          >
            <DMSuiteIcon :icon="DASHBOARD_ICONS.check" :size="16" custom-class="text-emerald-500 shrink-0" />
            {{ item }}
          </li>
        </ul>
      </div>

      <DialogFooter class="grid grid-cols-2 gap-3 pt-2">
        <Button
          variant="outline"
          class="w-full rounded-xl font-bold text-xs py-3 border-slate-200 text-slate-500 hover:bg-slate-50"
          @click="close"
        >
          Cancel
        </Button>
        <Button
          class="w-full rounded-xl bg-indigo-600 text-white font-bold text-xs py-3 hover:bg-indigo-700 flex items-center justify-center gap-1.5 shadow-md shadow-indigo-600/10 active:scale-98 transition duration-200"
          @click="confirm"
        >
          <DMSuiteIcon :icon="DASHBOARD_ICONS.download" :size="16" />
          Download PDF
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
